"use client";

import React, { useState } from "react";
import { TriangleAlert as AlertTriangle, ArrowRight, CircleCheck as CheckCircle2, Droplets, Flame, PhoneCall, Search, Trash2, Waves, Wrench, Zap } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
interface SymptomCategory {
  id: string;
  name: string;
  label: string;
}
interface FixtureItem {
  id: string;
  category: string;
  title: string;
  iconName: "toilet" | "faucet" | "heater" | "drain" | "disposal" | "pipe";
  urgency: "Emergency" | "High Priority" | "Prompt Service";
  urgencyVariant: "destructive" | "default" | "secondary";
  symptoms: string[];
  recommendedAction: string;
  pageUrl: string;
  serviceName: string;
}
const categories: SymptomCategory[] = [{
  id: "all",
  name: "All Fixtures",
  label: "All Problems"
}, {
  id: "toilet",
  name: "Toilets",
  label: "Toilets"
}, {
  id: "water-heater",
  name: "Water Heaters",
  label: "Water Heaters"
}, {
  id: "drains",
  name: "Drains & Sewers",
  label: "Drains & Sewers"
}, {
  id: "faucets-sinks",
  name: "Faucets & Sinks",
  label: "Faucets & Sinks"
}, {
  id: "disposal-pipes",
  name: "Disposals & Pipes",
  label: "Disposals & Pipes"
}];
const fixtureData: FixtureItem[] = [{
  id: "toilet-overflow",
  category: "toilet",
  title: "Toilet Issues & Backups",
  iconName: "toilet",
  urgency: "Emergency",
  urgencyVariant: "destructive",
  symptoms: ["Water rising rapidly after flushing", "Constant running or phantom flushing", "Gurgling sounds from shower/tub when flushed", "Leaks pooling around base bolts or wax ring"],
  recommendedAction: "Shut off water valve behind toilet immediately.",
  pageUrl: "/toilet-repair/",
  serviceName: "Toilet Repair Services"
}, {
  id: "water-heater-failure",
  category: "water-heater",
  title: "Water Heater Malfunctions",
  iconName: "heater",
  urgency: "High Priority",
  urgencyVariant: "destructive",
  symptoms: ["No hot water or inconsistent warm water", "Popping, rumbling, or whistling noises from tank", "Rusty or foul-smelling hot tap water", "Moisture or pooling water around tank base"],
  recommendedAction: "Turn off power/gas supply if water is actively pooling.",
  pageUrl: "/water-heater-repair/",
  serviceName: "Water Heater Repair"
}, {
  id: "clogged-drains",
  category: "drains",
  title: "Slow or Blocked Drains",
  iconName: "drain",
  urgency: "High Priority",
  urgencyVariant: "default",
  symptoms: ["Standing water in sinks, tubs, or showers", "Multiple slow drains throughout the house", "Sewage odor coming from floor or sink drains", "Water backing up into nearby fixtures"],
  recommendedAction: "Avoid chemical drain cleaners which corrode pipes.",
  pageUrl: "/drain-cleaning/",
  serviceName: "Drain Cleaning Services"
}, {
  id: "faucet-sink-leaks",
  category: "faucets-sinks",
  title: "Faucets & Sink Leaks",
  iconName: "faucet",
  urgency: "Prompt Service",
  urgencyVariant: "secondary",
  symptoms: ["Persistent dripping wasting water", "Low water pressure only at specific taps", "Dampness or swelling under sink cabinets", "Spraying or sputtering aerator nozzles"],
  recommendedAction: "Locate under-sink stop valves if spraying persists.",
  pageUrl: "/faucet-sink-repair/",
  serviceName: "Faucet & Sink Repair"
}, {
  id: "disposal-jam",
  category: "disposal-pipes",
  title: "Garbage Disposal Trouble",
  iconName: "disposal",
  urgency: "Prompt Service",
  urgencyVariant: "secondary",
  symptoms: ["Humming motor with stationary blades", "Total lack of power when switched on", "Water leaking into cabinet below unit", "Persistent foul odor despite cleaning"],
  recommendedAction: "Never place hands inside the unit; turn off power.",
  pageUrl: "/garbage-disposal-repair/",
  serviceName: "Garbage Disposal Repair"
}, {
  id: "burst-pipe-leaks",
  category: "disposal-pipes",
  title: "Pipe Leaks & Low Pressure",
  iconName: "pipe",
  urgency: "Emergency",
  urgencyVariant: "destructive",
  symptoms: ["Sudden drop in entire home water pressure", "Hissing or dripping sounds inside drywall", "Unexplained ceiling spots or wet carpets", "Water meter spinning with all taps closed"],
  recommendedAction: "Shut down the main water meter shut-off valve immediately.",
  pageUrl: "/pipe-repair/",
  serviceName: "Pipe Repair & Repiping"
}];
export default function FixtureTriage() {
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const prefersReducedMotion = useReducedMotion();
  const filteredFixtures = fixtureData.filter(item => {
    const matchesCategory = selectedCategory === "all" || item.category === selectedCategory;
    const query = searchQuery.toLowerCase().trim();
    if (!query) return matchesCategory;
    const matchesSearch = item.title.toLowerCase().includes(query) || item.serviceName.toLowerCase().includes(query) || item.symptoms.some(s => s.toLowerCase().includes(query));
    return matchesCategory && matchesSearch;
  });
  const renderIcon = (name: FixtureItem["iconName"]) => {
    switch (name) {
      case "toilet":
        return <Waves className="size-6 text-primary" />;
      case "heater":
        return <Flame className="size-6 text-accent-foreground" />;
      case "drain":
        return <Droplets className="size-6 text-primary" />;
      case "faucet":
        return <Wrench className="size-6 text-primary" />;
      case "disposal":
        return <Trash2 className="size-6 text-primary" />;
      case "pipe":
        return <Zap className="size-6 text-destructive" />;
      default:
        return <Wrench className="size-6 text-primary" />;
    }
  };
  return <section id="fixturesymptomstriage" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header Block */}
        <div className="max-w-3xl mx-auto text-center mb-12">
          <Badge variant="outline" className="mb-4 text-xs font-semibold uppercase tracking-wider px-3 py-1 bg-secondary text-secondary-foreground border-border">
            Fast Symptom Diagnosis
          </Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight mb-4">
            Identify Your Plumbing Issue
          </h2>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            Select your affected fixture or search your symptoms below to pinpoint the problem and connect with our Jonesboro 24/7 emergency dispatch.
          </p>
        </div>

        {/* Filter Controls & Search */}
        <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4 mb-10 pb-6 border-b border-border">
          {/* Category Pills */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0 scrollbar-none">
            {categories.map((cat, index) => <Button key={cat.id} data-index={index} variant={selectedCategory === cat.id ? "default" : "outline"} size="sm" onClick={() => setSelectedCategory(cat.id)} className={`rounded-full transition-colors whitespace-nowrap cursor-pointer ${selectedCategory === cat.id ? "bg-primary text-primary-foreground hover:bg-primary/90 shadow-xs" : "bg-background text-muted-foreground hover:text-foreground hover:bg-secondary/70 border-border"}`}>
                {cat.name}
              </Button>)}
          </div>

          {/* Search Box */}
          <div className="relative w-full md:w-72 shrink-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
            <input type="text" placeholder="Search symptom (e.g. leak, noise)..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm bg-card text-foreground border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all placeholder:text-muted-foreground" />
          </div>
        </div>

        {/* Symptoms Grid */}
        {filteredFixtures.length === 0 ? <div className="text-center py-16 bg-card border border-border rounded-xl">
            <AlertTriangle className="size-10 text-accent mx-auto mb-3" />
            <h3 className="text-lg font-default font-semibold text-foreground mb-1">
              No matching symptoms found
            </h3>
            <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
              Not sure what is wrong? Contact our emergency team directly for immediate assistance in Jonesboro, GA.
            </p>
            <div className="flex items-center justify-center gap-3">
              <Button variant="outline" size="sm" onClick={() => {
            setSelectedCategory("all");
            setSearchQuery("");
          }}>
                Reset Filters
              </Button>
              <Button asChild size="sm" variant="default">
                <Link to="/contact/">
                  Contact Plumber
                </Link>
              </Button>
            </div>
          </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredFixtures.map((item, index) => <motion.div key={item.id} data-index={index} initial={prefersReducedMotion ? false : {
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.25,
          delay: prefersReducedMotion ? 0 : index * 0.05
        }} className="h-full">
                <Card className="h-full flex flex-col justify-between border-border bg-card shadow-sm hover:shadow-md hover:border-primary/40 transition-all rounded-xl overflow-hidden">
                  <div>
                    <CardHeader className="pb-3 border-b border-border/60 bg-muted/20">
                      <div className="flex items-start justify-between gap-2">
                        <div className="p-2.5 rounded-lg bg-background border border-border shadow-2xs">
                          {renderIcon(item.iconName)}
                        </div>
                        <Badge variant={item.urgencyVariant} className="font-semibold text-2xs uppercase tracking-wide">
                          {item.urgency}
                        </Badge>
                      </div>
                      <CardTitle className="text-xl font-default font-bold text-card-foreground mt-3">
                        {item.title}
                      </CardTitle>
                      <CardDescription className="text-xs text-muted-foreground font-medium">
                        Common indicator signals
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="pt-4 pb-2">
                      <ul className="space-y-2.5 mb-4">
                        {item.symptoms.map((symptom, symptomIndex) => <li key={symptomIndex} className="flex items-start gap-2.5 text-sm text-foreground/90">
                            <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                            <span className="leading-snug">{symptom}</span>
                          </li>)}
                      </ul>

                      <div className="p-3 bg-secondary/60 rounded-md border border-border text-xs text-secondary-foreground">
                        <span className="font-semibold text-foreground block mb-0.5">Quick First Step:</span>
                        {item.recommendedAction}
                      </div>
                    </CardContent>
                  </div>

                  <CardFooter className="pt-3 pb-5 border-t border-border/60 flex items-center justify-between gap-3">
                    <Button asChild variant="outline" size="sm" className="w-full justify-between font-semibold group hover:bg-primary hover:text-primary-foreground hover:border-primary">
                      <Link to={item.pageUrl}>
                        <span>{item.serviceName}</span>
                        <ArrowRight className="size-4 group-hover:translate-x-0.5 transition-transform" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>)}
          </div>}

        {/* Bottom Fast Help Banner */}
        <div className="mt-14 rounded-2xl bg-primary text-primary-foreground p-6 md:p-8 shadow-lg flex flex-col md:flex-row items-center justify-between gap-6 border border-primary/20">
          <div className="space-y-1.5 text-center md:text-left">
            <h3 className="text-xl md:text-2xl font-default font-bold tracking-tight">
              Plumbing emergency unfolding right now?
            </h3>
            <p className="text-primary-foreground/90 text-sm md:text-base max-w-xl">
              Do not wait for structural water damage. Our Jonesboro local dispatch technicians are standing by 24/7.
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3 shrink-0">
            <Button asChild size="lg" variant="secondary" className="font-semibold shadow-md">
              <Link to="/emergency-plumbing/">
                <PhoneCall className="size-4" />
                <span>24/7 Emergency Service</span>
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="bg-transparent border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground/10">
              <Link to="/contact/">
                <span>Request Inspection</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}